import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FormsModule } from '@angular/forms';
import { Table } from 'primeng/table';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService } from 'primeng/api';

import { TranslateService } from '../../../../services/translate.service';
import { MaintainAccountService } from '../maintain-account.service';

import { Subject, takeUntil } from 'rxjs';

import { ProfilersComponent } from '../../create-account/steps/profilers/profilers.component';
import { Profiler } from '../../../../models/user.model';

import { Pagination } from '../../../../services/pagination.abstruct';

@Component({
	selector: 'app-tab-profilers',
	standalone: true,
	imports: [
		CommonModule,
		TableModule,
		TagModule,
		TooltipModule,
		ButtonModule,
		ProgressSpinnerModule,
		FormsModule
	],
	templateUrl: './tab-profilers.component.html',
	styleUrls: ['./tab-profilers.component.scss'],
	providers: [DialogService, MessageService]
})
export class TabProfilersComponent extends Pagination implements OnInit, OnDestroy {
	@Input() profiles!: Profiler[];
	@Input() profilesLoading!: boolean;
	@Input() profilesError!: boolean;

	@Input() customerRecId!: number;

	@ViewChild('dt') dt!: Table;

	ref: DynamicDialogRef | undefined;
	private destroy$ = new Subject<void>();

	globalFilter: string = '';
	globalFilterFields: string[] = [];

	constructor(
		public translateServ: TranslateService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private maintainAccountService: MaintainAccountService,
		private dialogService: DialogService
	) {super(); }

	ngOnInit(): void {
		if (this.profiles?.length > 0) {
			this.globalFilterFields = Object.keys(this.profiles[0]);
		}
	}

	ngOnDestroy(): void {
		if (this.ref) {
			this.ref.close();
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	getProfilerSeverity(isActive: number): string {
		return isActive === 1 ? 'success' : 'danger';
	}

	/** Open editor for existing record */
	editProfiler(profiler: Profiler): void {
		this.ref = this.dialogService.open(ProfilersComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE_EDIT'] || 'Edit Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: false,
				customerRecId: this.customerRecId,
				profilerEntry: 
				{
					id : profiler.RecID,
					recId : profiler.ProfileRecID,
					profile : profiler.Profile,
					description : profiler.Descr,
					isActive : profiler.IsActive,
					isApproved : profiler.IsApproved
				}
			}
		});

		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_UPDATE_SUCCESS_MSG'] || 'Record updated successfully.',
					life: 3000
				});
				this.reloadProfilersForUser();
			}
		});
	}

	/** Create new record */
	createNewRecord(): void {
		this.ref = this.dialogService.open(ProfilersComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE'] || 'Create New Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: true,
				customerRecId: this.customerRecId
			}
		});

		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_CREATE_SUCCESS_MSG'] || 'Record created successfully.',
					life: 3000
				});
				this.reloadProfilersForUser();
			}
		});
	}

	/** Toggle IsActive */
	toggleProfilerStatus(prof: Profiler, action: boolean): void {
			const newStatus = action ? (prof.IsActive === 1 ? 0 : 1) : (prof.IsApproved === 1 ? 0 : 1);
			const actionText = newStatus === 1 ?
				(this.translateServ.dictionary['ACTIVATE'] || 'activate') :
				(this.translateServ.dictionary['DEACTIVATE'] || 'deactivate');
	
			this.confirmationService.confirm({
				message: `${this.translateServ.dictionary['CONFIRM_CHANGE_STATUS'] || 'Are you sure you want to'} ${actionText} ${prof.Profile}?`,
				header: this.translateServ.dictionary['CONFIRM_ACTION'] || 'Confirm Action',
				icon: 'pi pi-exclamation-triangle',
				acceptLabel: this.translateServ.dictionary['YES'] || 'Yes',
				rejectLabel: this.translateServ.dictionary['NO'] || 'No',
				accept: () => {
					this.updateProfilerStatus(action, prof.RecID, newStatus);
				},
				reject: () => {
					this.messageService.add({
						severity: 'info',
						summary: this.translateServ.dictionary['CANCELLED'] || 'Cancelled',
						detail: this.translateServ.dictionary['ACTION_CANCELLED'] || 'Action cancelled.',
						life: 3000
					});
				}
			});
	}

	updateProfilerStatus(source: boolean, recId: number, newStatus: 0 | 1): void {
		// Calling the API service to update the status
		this.maintainAccountService.updateFunctions(source, this.constructor.name.substring(1), recId, newStatus).subscribe({
			next: (response: any) => {
				// Check if the API response indicates a successful update
				if (response && response[0]?.RecID && response[0].RecID !== -1) {
					const index = this.profiles?.findIndex(name => name.RecID === recId);
					if (this.profiles && index !== undefined && index > -1) {
						// Correctly updating the 'IsActive' or 'IsApproved' property
						if (source) {
							this.profiles[index].IsActive = newStatus;
						}
						else {
							this.profiles[index].IsApproved = newStatus;
						}

						const statusText = newStatus === 1 ? 'Active' : 'Inactive';
						this.messageService.add({
							severity: 'success',
							summary: this.translateServ.dictionary['STATUS_UPDATED'] || 'Status Updated',
							// Correctly using the 'Street' property for the message
							detail: `Profile "${this.profiles[index].Profile}" is now ${statusText}.`,
							life: 3000
						});
					}
				} else {
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Record not found or invalid response.',
						life: 5000
					});
				}
			},
			error: (err: any) => {
				console.error('API Error:', err);
				this.messageService.add({
					severity: 'error',
					summary: this.translateServ.dictionary['ERROR'] || 'Error',
					detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Please try again.',
					life: 5000
				});
			}
		});
	}

	/** Delete record */
	confirmDelete(profiler: Profiler): void {
		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['MAINT_DELETE_CONFIRM_MSG'] || 'Are you sure you want to delete this record?'} (ID: ${profiler.RecID})`,
			header: this.translateServ.dictionary['MAINT_DELETE_CONFIRM_HEADER'] || 'Confirm Deletion',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['CONFIRM_DELETE'] || 'Delete',
			rejectLabel: this.translateServ.dictionary['CONFIRM_CANCEL'] || 'Cancel',
			accept: () => {
				this.maintainAccountService.deleteRecord(this.constructor.name.substring(1), profiler.RecID)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: () => {
							this.messageService.add({
								severity: 'success',
								summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
								detail: this.translateServ.dictionary['MAINT_DELETE_SUCCESS_MSG'] || 'Record deleted successfully.',
								life: 3000
							});
							this.reloadProfilersForUser();
						}
					});
			}
		});
	}

	/** Reload data */
	private reloadProfilersForUser(): void {
		const index = this.maintainAccountService.getMappingIndex('profiles');
		const instance = this.maintainAccountService.getUserDataMappings();
		this.maintainAccountService.getDatas(this.customerRecId, instance[index].apiName, instance[index].special)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => {
					this.profiles = data as Profiler[];; 
					this.profilesLoading = false;
				},
				error: (error) => {
					console.error('Failed to reload apayment info:', error);
					this.profilesError = true;
					this.profilesLoading = false;
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['MAINT_LOAD_FAILED_MSG_PAYMENT'] || 'Failed to load payment info.',
						life: 5000
					});
				}
			});
	}


	applyGlobalFilter(): void {
		this.dt.filterGlobal(this.globalFilter, 'contains');
	}
}
